import {
  LAB_ATTACHMENT_IN_LABPORTALEN_LABEL,
  type ClinicianLabOrder,
} from "@care-by-ella/contracts";
import { BUCKET_BADGE_CLASS, BUCKET_LABEL } from "@/lib/lab-labels";
import {
  LAB_CORRECTED_LABEL,
  LAB_EARLIER_VALUE_LABEL,
  LAB_MISSING_VALUES_LABEL,
  LAB_ORDER_COMMENT_LABEL,
  LAB_PARTIAL_DELIVERY_NOTE,
  LAB_PRELIMINARY_LABEL,
  LAB_REFERENCE_LABEL,
  LAB_VALUE_COMMENT_LABEL,
  labOrderResultsView,
} from "@/lib/lab-order-results";

// TES-1337 — on a lab order on the Blodprov tab: what the lab delivered. The
// lab's comment on the order above the values, each value's own lab comment
// under it, verbatim, and on a partial delivery which values are still
// missing. Read-only; the doctor's feedback to the patient is elsewhere
// (LabFeedbackPanel) and never mixed with the lab's words.
//
// TES-1341 — a value a lab correction replaced is marked, with the values it
// replaced under it; and a file the lab attached is named, to be opened in
// LabPortalen (Ella does not store it).
export function LabOrderResults({
  order,
}: {
  order: Pick<
    ClinicianLabOrder,
    "results" | "labComments" | "missingAnalytes" | "labAttachments"
  >;
}) {
  const view = labOrderResultsView(order);
  if (view.isEmpty) return null;
  return (
    <div className="mt-sm flex flex-col gap-sm" data-testid="lab-order-results">
      {view.orderComments.length > 0 ? (
        <div
          className="rounded-sm border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm"
          data-testid="lab-order-lab-comment"
        >
          <p className="text-xs font-semibold text-textSecondary">
            {LAB_ORDER_COMMENT_LABEL}
          </p>
          {view.orderComments.map((c) => (
            <p key={c.key} className="mt-[2px] whitespace-pre-wrap">
              {c.when ? (
                <span className="text-xs text-textSecondary">{c.when}: </span>
              ) : null}
              {c.text}
            </p>
          ))}
        </div>
      ) : null}
      {view.attachments.length > 0 ? (
        <div
          className="rounded-sm border border-surfaceMuted bg-surfaceMuted/30 p-sm text-xs"
          data-testid="lab-order-attachments"
        >
          <p className="font-semibold text-textSecondary">
            {LAB_ATTACHMENT_IN_LABPORTALEN_LABEL}
          </p>
          {view.attachments.some(Boolean) ? (
            <p className="text-textPrimary">
              {view.attachments.filter(Boolean).join(", ")}
            </p>
          ) : null}
        </div>
      ) : null}
      {view.missing.length > 0 ? (
        <div
          className="rounded-sm border border-warning/30 bg-warning/5 p-sm text-xs"
          data-testid="lab-order-missing-values"
        >
          <p className="font-semibold text-warning">
            {LAB_PARTIAL_DELIVERY_NOTE}
          </p>
          <p className="text-textPrimary">
            {LAB_MISSING_VALUES_LABEL}: {view.missing.join(", ")}
          </p>
        </div>
      ) : null}
      {view.rows.length > 0 ? (
        <ul className="flex flex-col gap-xs">
          {view.rows.map((r) => (
            <li
              key={r.analyteKey}
              className="border-b border-surfaceMuted pb-xs last:border-b-0"
            >
              <div className="flex items-center justify-between gap-sm text-sm">
                <span className="font-medium">{r.name}</span>
                <span className="flex flex-wrap items-center justify-end gap-xs">
                  {r.corrected ? (
                    <span
                      className="rounded-pill border border-warning/40 bg-warning/10 px-xs py-[2px] text-[11px] text-warning"
                      data-testid="lab-value-corrected"
                    >
                      {LAB_CORRECTED_LABEL}
                    </span>
                  ) : null}
                  {r.preliminary ? (
                    <span
                      className="rounded-pill border border-warning/40 bg-warning/10 px-xs py-[2px] text-[11px] text-warning"
                      data-testid="lab-value-preliminary"
                    >
                      {LAB_PRELIMINARY_LABEL}
                    </span>
                  ) : null}
                  <span>{r.value}</span>
                  {r.range ? (
                    <span className="text-xs text-textSecondary">
                      (ref {r.range})
                    </span>
                  ) : null}
                  <span
                    className={`rounded-pill border px-xs py-[2px] text-[11px] ${BUCKET_BADGE_CLASS[r.bucket]}`}
                  >
                    {BUCKET_LABEL[r.bucket]}
                  </span>
                </span>
              </div>
              {r.earlier.map((e, i) => (
                <p
                  key={i}
                  className="mt-[2px] text-xs text-textSecondary"
                  data-testid="lab-value-earlier"
                >
                  {LAB_EARLIER_VALUE_LABEL}: {e.value} ({e.when})
                </p>
              ))}
              {r.labReference ? (
                <p className="mt-[2px] text-xs text-textSecondary">
                  {LAB_REFERENCE_LABEL}: {r.labReference}
                </p>
              ) : null}
              {r.labComment ? (
                <p
                  className="mt-[2px] whitespace-pre-wrap text-xs text-textSecondary"
                  data-testid="lab-value-lab-comment"
                >
                  <span className="font-semibold">
                    {LAB_VALUE_COMMENT_LABEL}:
                  </span>{" "}
                  {r.labComment}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
